<script setup lang="ts">
/**
 * Флаги фич — макет 91:834: подсказка про git, таблица с парой тумблеров и
 * блок последних переключений.
 *
 * Источник правды — файлы features/dev.json и features/prod.json в
 * k8s-infrastructure; трекер только показывает состояние и коммитит правку.
 * Поэтому «что включает» взять неоткуда: в файле лежит пара «ключ — включён»,
 * без описания. Вместо описания показываем задачу из ключа: по договорённости
 * ключ начинается с её номера (docs/design-gaps.md).
 */
import { AppButton, AppSwitch, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, Flag, History, Search } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useAuthStore } from '@/features/auth/store'
import {
  FLAG_ENVIRONMENTS,
  listActions,
  listFlags,
  setFlag,
  toFlagChanges,
  toFlagRows,
  withLastChange,
  type FlagEnvironment,
  type FlagRow,
} from '@/features/devops/api'
import NewFlagDialog from '@/features/devops/components/NewFlagDialog.vue'
import { initials } from '@/features/devops/format'
import { formatStamp } from '@/features/issues/format'

const { t, locale } = useI18n()
const auth = useAuthStore()

const rows = ref<FlagRow[]>([])
const changes = ref<Awaited<ReturnType<typeof loadChanges>>>([])
const isLoading = ref(false)
const error = ref('')
const busyKey = ref('')
const search = ref('')
const envFilter = ref<string[]>([])
const isNewOpen = ref(false)

const allowed = computed(() => auth.can('tracker:devops'))

const envOptions = FLAG_ENVIRONMENTS.map((value) => ({ value, label: value }))

/** Задача флага — из его ключа: `dev-1415-runtime-flags` → `DEV-1415`. */
const issueKeyOf = (key: string) => key.match(/^([a-z]+)-(\d+)/i)?.slice(1, 3).join('-').toUpperCase() ?? ''

const visible = computed(() => {
  const needle = search.value.trim().toLowerCase()

  return rows.value.filter((row) => {
    if (envFilter.value.length) {
      const on = envFilter.value.some((env) => row.enabled[env as FlagEnvironment])

      if (!on) return false
    }

    return !needle || row.key.toLowerCase().includes(needle)
  })
})

const loadChanges = async () => toFlagChanges(await listActions())

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    const [dev, prod, history] = await Promise.all([
      listFlags('dev'),
      listFlags('prod'),
      loadChanges(),
    ])

    rows.value = withLastChange(toFlagRows([...dev, ...prod]), history)
    changes.value = history
  } catch {
    error.value = t('devops.flags.error')
  } finally {
    isLoading.value = false
  }
}

const toggle = async (row: FlagRow, environment: FlagEnvironment, enabled: boolean) => {
  busyKey.value = `${row.key}:${environment}`

  try {
    await setFlag(row.key, environment, enabled)
    await load()
  } catch {
    error.value = t('devops.flags.toggleError')
    // Возвращаем тумблер на место: правка не доехала, а вид уже изменился.
    row.enabled[environment] = !enabled
  } finally {
    busyKey.value = ''
  }
}

const stamp = (iso: string) =>
  formatStamp(iso, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[{ label: t('nav.devops'), to: '/devops' }, { label: t('devops.flags.title') }]"
  >
    <FilterChip
      v-model="envFilter"
      :label="t('devops.flags.filters.environment')"
      :options="envOptions"
      :empty-text="t('devops.flags.filters.empty')"
    />

    <div class="flags__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('devops.flags.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('devops.flags.searchPlaceholder')"
      />
    </div>

    <AppButton v-if="allowed" variant="primary" size="small" @click="isNewOpen = true">
      {{ t('devops.flags.new') }}
    </AppButton>
  </TrackerToolbar>

  <div class="flags">
    <p class="flags__note">{{ t('devops.flags.note') }}</p>

    <div v-if="isLoading && !rows.length" class="flags__skeleton" />

    <TrackerEmptyState
      v-else-if="error && !rows.length"
      :icon="AlertTriangle"
      :title="t('devops.flags.error')"
      :description="t('devops.flags.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!visible.length"
      :icon="Flag"
      :title="t('devops.flags.empty.title')"
      :description="t('devops.flags.empty.description')"
    />

    <div v-else class="flags__table">
      <div class="flags__head">
        <span class="flags__col flags__col--key">{{ t('devops.flags.columns.flag') }}</span>
        <span class="flags__col flags__col--what">{{ t('devops.flags.columns.what') }}</span>
        <span class="flags__col flags__col--env">dev</span>
        <span class="flags__col flags__col--env">prod</span>
        <span class="flags__col flags__col--changed">{{ t('devops.flags.columns.changed') }}</span>
      </div>

      <div v-for="row in visible" :key="row.key" class="flags__row">
        <Flag class="flags__icon" :size="16" />
        <span class="flags__cell flags__cell--key">{{ row.key }}</span>
        <span class="flags__cell flags__cell--what">
          <RouterLink
            v-if="issueKeyOf(row.key)"
            class="flags__link"
            :to="`/issue/${issueKeyOf(row.key)}`"
          >
            {{ issueKeyOf(row.key) }}
          </RouterLink>
          <span v-else class="flags__muted">{{ t('devops.flags.noIssue') }}</span>
        </span>
        <span v-for="env in FLAG_ENVIRONMENTS" :key="env" class="flags__cell flags__cell--env">
          <AppSwitch
            :model-value="row.enabled[env]"
            size="small"
            :disabled="!allowed || busyKey === `${row.key}:${env}`"
            :aria-label="`${row.key} · ${env}`"
            @update:model-value="toggle(row, env, $event)"
          />
        </span>
        <span class="flags__cell flags__cell--changed" :title="row.updatedBy">
          {{ row.updatedBy ? `${initials(row.updatedBy)} · ${stamp(row.updatedAt)}` : '—' }}
        </span>
      </div>
    </div>

    <template v-if="changes.length">
      <h2 class="flags__overline">{{ t('devops.flags.recent') }}</h2>

      <div class="flags__table">
        <div v-for="change in changes" :key="change.id" class="flags__change">
          <History class="flags__icon" :size="15" />
          <span class="flags__cell flags__cell--key">{{ change.key }}</span>
          <span class="flags__change-text">
            {{
              t(change.enabled ? 'devops.flags.turnedOn' : 'devops.flags.turnedOff', {
                environment: change.environment,
              })
            }}
          </span>
          <span class="flags__change-actor">{{ change.actor }}</span>
          <span class="flags__spacer" />
          <span class="flags__change-at">{{ stamp(change.at) }}</span>
        </div>
      </div>
    </template>
  </div>

  <NewFlagDialog v-if="isNewOpen" @done="((isNewOpen = false), load())" @close="isNewOpen = false" />
</template>

<style scoped>
.flags {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 28px 48px;
}

.flags__note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.flags__skeleton {
  height: 370px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.flags__table {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.flags__head,
.flags__row,
.flags__change {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.flags__head {
  height: 34px;
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
}

.flags__row {
  height: 56px;
}

.flags__change {
  height: 44px;
}

.flags__row:last-child,
.flags__change:last-child {
  border-bottom: none;
}

.flags__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.flags__col--key,
.flags__cell--key {
  width: 260px;
}

.flags__col--what,
.flags__cell--what {
  flex: 1;
  min-width: 0;
}

.flags__col--env,
.flags__cell--env {
  width: 56px;
}

.flags__col--changed,
.flags__cell--changed {
  width: 180px;
}

.flags__cell {
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.flags__cell--key {
  color: var(--color-text-primary);
}

.flags__cell--what {
  font-family: inherit;
  font-size: var(--font-size-body);
}

.flags__link {
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.flags__link:hover {
  text-decoration: underline;
}

.flags__muted {
  color: var(--color-text-muted);
}

.flags__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.flags__change-text {
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
}

.flags__change-actor {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.flags__change-at {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.flags__spacer {
  flex: 1;
  min-width: 0;
}

.flags__search {
  width: 220px;
}
</style>
